import { confirmedCommunication } from './confirmedCommunication';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { resolveFrontendApiBase } from '../utils/apiBase';
import {
  Alert,
  Switch,
  Button,
  Card,
  Col,
  Divider,
  Empty,
  Form,
  Image,
  Input,
  InputNumber,
  message,
  Row,
  Space,
  Spin,
  Typography,
  Upload,
  theme,
} from 'antd';
import {
  CopyOutlined,
  DeleteOutlined,
  MailOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  SaveOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import adminAxios from './adminAxios';
import './AdminContentEditors.css';

const { Text, Title } = Typography;

const API_BASE = resolveFrontendApiBase();

const LANGUAGE_POPULATION_ESTIMATES: Record<string, number> = {
  arabic: 310_000_000,
  bengali: 265_000_000,
  chinese: 1_100_000_000,
  dutch: 29_000_000,
  english: 1_500_000_000,
  french: 310_000_000,
  german: 134_000_000,
  hindi: 600_000_000,
  italian: 85_000_000,
  japanese: 125_000_000,
  korean: 82_000_000,
  polish: 45_000_000,
  portuguese: 265_000_000,
  russian: 258_000_000,
  spanish: 559_000_000,
  swedish: 10_000_000,
  turkish: 88_000_000,
  urdu: 230_000_000,
  vietnamese: 86_000_000,
};
interface YearEndEmailConfig {
  brandName?: string;
  senderName?: string;
  senderEmail?: string;
  supportEmail?: string;
  dashboardUrl?: string;
  primaryColor?: string;
  textMain?: string;
  textMuted?: string;
  cardBackground?: string;
  bodyBackground?: string;
  borderColor?: string;
  logoImage?: string;
  mascotImage?: string;
}

interface YearEndEmailStat {
  label?: string;
  value?: string;
  detail?: string;
}

interface YearEndLanguageBreakdown {
  language?: string;
  words?: number;
}

interface YearEndTry2026 {
  title?: string;
  description?: string;
}

interface YearEndEmailSample {
  userName?: string;
  year?: number;
  headline?: string;
  summaryLine?: string;
  topPercentile?: number;
  topPercentileMetric?: string;
  topPercentileDetail?: string;
  stats?: YearEndEmailStat[];
  languageBreakdown?: YearEndLanguageBreakdown[];
  try2026?: YearEndTry2026[];
  ctaLabel?: string;
  ctaUrl?: string;
  disclaimer?: string;
  recipientEmail?: string;
}

interface YearEndEmailPreview {
  subject: string;
  html: string;
  text: string;
}

interface YearEndEmailAdminResponse {
  config: YearEndEmailConfig;
  preview: YearEndEmailPreview;
}

interface YearEndRebuildResponse {
  jobId: string;
  status: string;
  processedDocs: number;
  updatedUsers: number;
  lastDocId?: string | null;
  done: boolean;
}

interface YearEndRebuildCountResponse {
  year: number;
  count: number;
  source: string;
}

interface YearEndEmailSnapshotResponse {
  jobId: string;
  status: string;
  processedUsers: number;
  computedUsers: number;
  skippedUsers: number;
  lastUserId?: string | null;
  done: boolean;
}

interface YearEndEmailSendResponse {
  jobId: string;
  status: string;
  processedUsers: number;
  sentCount: number;
  skippedCount: number;
  failedCount: number;
  lastUserId?: string | null;
  done: boolean;
}

interface YearEndRandomTestSendResponse {
  year: number;
  requested: number;
  sent: number;
  failed: number;
  skipped: number;
  userIds: string[];
}
interface YearEndUserStatsResponse {
  userId: string;
  year: number;
  totalBooks: number;
  booksAddedThisYear: number;
  translatedBooks: number;
  translatedChapters: number;
  translatedWords: number;
  topTargetLanguage?: string | null;
  topTargetLanguageWords?: number;
  mostTranslatedTitle?: string | null;
  mostTranslatedTitleWords?: number;
  audioHoursGenerated?: number;
  mostUsedModel?: string | null;
  mostUsedModelWords?: number;
  topLanguagePercentile?: number | null;
  topLanguagePercentileLanguage?: string | null;
  topLanguagePercentileDetail?: string | null;
  proofreadWords?: number;
  coverImagesGenerated?: number;
  topPercentile?: number | null;
  topPercentileMetric?: string | null;
  topPercentileDetail?: string | null;
  wordsByLanguage?: Record<string, number>;
}

const DEFAULT_SAMPLE: YearEndEmailSample = {
  userName: 'Avery',
  year: 2025,
  headline: 'Your 2025 ScribeShadow Wrapped',
  summaryLine: 'You brought 6 books to life across 4 languages.',
  topPercentile: 5,
  topPercentileMetric: 'words translated',
  topPercentileDetail: 'You landed in the top 5% of translators by total words.',
  stats: [
    { label: 'Words translated', value: '412,000', detail: "That's ~1.1 days of non-stop reading." },
    { label: 'Chapters completed', value: '182', detail: 'That averages 2,264 words per chapter.' },
    { label: 'Favorite target language', value: 'Spanish', detail: 'This brings your books to ~559M speakers worldwide.' },
    { label: 'Proofread words', value: '128,400', detail: "That's ~106.9 hours saved." },
    { label: 'Audio hours generated', value: '19.5', detail: "That's about 13 daily commutes." },
    { label: 'Cover images generated', value: '14', detail: 'Less time in Canva, more time writing.' },
  ],
  languageBreakdown: [
    { language: 'Spanish', words: 180000 },
    { language: 'French', words: 110000 },
    { language: 'German', words: 70000 },
    { language: 'Portuguese', words: 42000 },
  ],
  ctaLabel: 'Open your library',
  disclaimer: 'Stats are based on model usage tracking that began Sep 28, 2025.',
  recipientEmail: 'contact@scribeshadow.com',
};

const YearEndEmailAdmin: React.FC = () => {
  const { token } = theme.useToken();
  const [configForm] = Form.useForm<YearEndEmailConfig>();
  const [sampleForm] = Form.useForm<YearEndEmailSample>();
  const [loadedConfig, setLoadedConfig] = useState<YearEndEmailConfig | null>(null);
  const [preview, setPreview] = useState<YearEndEmailPreview | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);
  const [sendingTest, setSendingTest] = useState<boolean>(false);
  const [userStatsLoading, setUserStatsLoading] = useState<boolean>(false);
  const [userIdInput, setUserIdInput] = useState<string>('');
  const [rebuildLoading, setRebuildLoading] = useState<boolean>(false);
  const [rebuildJobId, setRebuildJobId] = useState<string>('');
  const [rebuildStatus, setRebuildStatus] = useState<YearEndRebuildResponse | null>(null);
  const [rebuildBatchSize, setRebuildBatchSize] = useState<number>(1500);
  const [autoRebuild, setAutoRebuild] = useState<boolean>(false);
  const rebuildTimerRef = React.useRef<number | null>(null);
  const [rebuildCount, setRebuildCount] = useState<YearEndRebuildCountResponse | null>(null);
  const [snapshotLoading, setSnapshotLoading] = useState<boolean>(false);
  const [snapshotJobId, setSnapshotJobId] = useState<string>('');
  const [snapshotStatus, setSnapshotStatus] = useState<YearEndEmailSnapshotResponse | null>(null);
  const [snapshotBatchSize, setSnapshotBatchSize] = useState<number>(200);
  const [autoSnapshot, setAutoSnapshot] = useState<boolean>(false);
  const snapshotTimerRef = React.useRef<number | null>(null);
  const [sendLoading, setSendLoading] = useState<boolean>(false);
  const [sendJobId, setSendJobId] = useState<string>('');
  const [sendStatus, setSendStatus] = useState<YearEndEmailSendResponse | null>(null);
  const [sendBatchSize, setSendBatchSize] = useState<number>(10);
  const [try2026Cards, setTry2026Cards] = useState<YearEndTry2026[]>([]);
  const [randomTestLoading, setRandomTestLoading] = useState<boolean>(false);
  const [randomTestResult, setRandomTestResult] = useState<YearEndRandomTestSendResponse | null>(null);
  const [autoSend, setAutoSend] = useState<boolean>(false);
  const sendTimerRef = React.useRef<number | null>(null);
  const [rebuildCountLoading, setRebuildCountLoading] = useState<boolean>(false);
  const [inlineError, setInlineError] = useState<string | null>(null);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setInlineError(null);
    try {
      const { data } = await adminAxios.get<YearEndEmailAdminResponse>(`${API_BASE}/admin/year-end-emails/config`);
      setLoadedConfig(data.config);
      configForm.setFieldsValue(data.config);
      sampleForm.setFieldsValue(DEFAULT_SAMPLE);
      setPreview(data.preview);
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      const msg = detail || 'Failed to load year-end email configuration.';
      setInlineError(msg);
      message.error(msg);
    } finally {
      setLoading(false);
    }
  }, [configForm, sampleForm]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleSaveConfig = async (values: YearEndEmailConfig) => {
    setSaving(true);
    setInlineError(null);
    try {
      await adminAxios.post(`${API_BASE}/admin/year-end-emails/config`, values);
      message.success('Year-end email configuration updated.');
      await loadConfig();
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      const msg = detail || 'Failed to update year-end email configuration.';
      setInlineError(msg);
      message.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const refreshPreview = async () => {
    const sampleValues = sampleForm.getFieldsValue();
    const configValues = configForm.getFieldsValue();
    setPreviewLoading(true);
    setInlineError(null);
    try {
      const { data } = await adminAxios.post<{ preview: YearEndEmailPreview }>(
        `${API_BASE}/admin/year-end-emails/preview`,
        {
          sample: { ...sampleValues, try2026: try2026Cards },
          configOverrides: configValues,
        }
      );
      setPreview(data.preview);
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      const msg = detail || 'Failed to refresh preview.';
      setInlineError(msg);
      message.error(msg);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSendTestEmail = async () => {
    const sampleValues = sampleForm.getFieldsValue();
    if (!sampleValues.recipientEmail) {
      message.warning('Enter a recipient email in the sample inputs to send a test.');
      return;
    }
    setSendingTest(true);
    setInlineError(null);
    try {
      await confirmedCommunication(`${API_BASE}/admin/year-end-emails/test-send`, {
        sample: { ...sampleValues, try2026: try2026Cards },
        configOverrides: configForm.getFieldsValue(),
        toEmail: sampleValues.recipientEmail,
      });
      message.success('Test email sent.');
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      const msg = detail || 'Failed to send test email.';
      setInlineError(msg);
      message.error(msg);
    } finally {
      setSendingTest(false);
    }
  };

  const formatNumber = (value?: number | null) => {
    if (!value && value !== 0) {
      return '0';
    }
    return new Intl.NumberFormat('en-US').format(value);
  };

  const formatHours = (value?: number | null) => {
    if (!value && value !== 0) {
      return '0';
    }
    if (value >= 10) {
      return String(Math.round(value));
    }
    return value.toFixed(1);
  };

  const formatDecimal = (value: number, places = 1) => {
    const formatted = value.toFixed(places);
    return formatted.endsWith('.0') ? formatted.slice(0, -2) : formatted;
  };

  const formatPopulation = (value?: number | null) => {
    if (!value) {
      return undefined;
    }
    if (value >= 1_000_000_000) {
      return `${formatDecimal(value / 1_000_000_000)}B`;
    }
    if (value >= 1_000_000) {
      return `${formatDecimal(value / 1_000_000)}M`;
    }
    return formatNumber(value);
  };

  const handleLoadUserStats = async () => {
    const trimmed = userIdInput.trim();
    if (!trimmed) {
      message.warning('Enter a user ID to load stats.');
      return;
    }
    const sampleValues = sampleForm.getFieldsValue();
    const yearValue = sampleValues.year || DEFAULT_SAMPLE.year || new Date().getFullYear();
    setUserStatsLoading(true);
    try {
      const { data } = await adminAxios.get<YearEndUserStatsResponse>(
        `${API_BASE}/admin/year-end-emails/user-stats/${encodeURIComponent(trimmed)}?year=${yearValue}&includePercentile=true`
      );
      const statsList: YearEndEmailStat[] = [];

      if (data.translatedWords > 0) {
        const readingMinutes = Math.round(data.translatedWords / 250);
        const readingDays = readingMinutes / (60 * 24);
        const readingDaysText = readingDays >= 10 ? String(Math.round(readingDays)) : formatDecimal(readingDays, 1);
        statsList.push({
          label: 'Words translated',
          value: formatNumber(data.translatedWords),
          detail: `That's ~${readingDaysText} days of non-stop reading.`,
        });
      }

      if (data.translatedChapters > 0) {
        const avgWords =
          data.translatedWords > 0
            ? Math.round(data.translatedWords / data.translatedChapters)
            : 0;
        statsList.push({
          label: 'Chapters completed',
          value: formatNumber(data.translatedChapters),
          detail: avgWords ? `That averages ${formatNumber(avgWords)} words per chapter.` : undefined,
        });
      }

      if (data.topTargetLanguage) {
        const displayLanguage =
          data.topTargetLanguage.charAt(0).toUpperCase() + data.topTargetLanguage.slice(1);
        const populationKey = data.topTargetLanguage.toLowerCase();
        const populationText = formatPopulation(LANGUAGE_POPULATION_ESTIMATES[populationKey]);
        statsList.push({
          label: 'Favorite target language',
          value: displayLanguage,
          detail: populationText
            ? `This brings your books to ~${populationText} speakers worldwide.`
            : 'This brings your books to a global audience.',
        });
      }

      if (data.proofreadWords && data.proofreadWords > 0) {
        const proofreadHours = data.proofreadWords / 1200;
        statsList.push({
          label: 'Proofread words',
          value: formatNumber(data.proofreadWords),
          detail: `That's ~${formatHours(proofreadHours)} hours saved.`,
        });
      }

      if (data.audioHoursGenerated && data.audioHoursGenerated > 0) {
        const commuteCount = Math.max(1, Math.round(data.audioHoursGenerated / 1.5));
        statsList.push({
          label: 'Audio hours generated',
          value: formatHours(data.audioHoursGenerated),
          detail: `That's about ${commuteCount} daily commutes.`,
        });
      }

      if (data.coverImagesGenerated && data.coverImagesGenerated > 0) {
        statsList.push({
          label: 'Cover images generated',
          value: String(data.coverImagesGenerated),
          detail: 'Less time in Canva, more time writing.',
        });
      }

      if (data.booksAddedThisYear && data.booksAddedThisYear > 0) {
        const feet = data.booksAddedThisYear / 12;
        const feetText = feet >= 10 ? String(Math.round(feet)) : formatDecimal(feet, 1);
        const meters = feet * 0.3048;
        const metersText = meters >= 10 ? String(Math.round(meters)) : formatDecimal(meters, 1);
        statsList.push({
          label: 'Library growth',
          value: `+${data.booksAddedThisYear} books`,
          detail: `That's ~${feetText} ft (~${metersText} m) of shelf space.`,
        });
      }

      const prioritizedStats = statsList.slice(0, 6);
      const try2026: YearEndTry2026[] = [];
      if (!data.audioHoursGenerated || data.audioHoursGenerated <= 0) {
        try2026.push({
          title: 'Try ScribeShadow Generated Audiobooks',
          description: 'Turn your translated chapters into audio so readers can listen anywhere.',
        });
      }
      if (!data.coverImagesGenerated || data.coverImagesGenerated <= 0) {
        try2026.push({
          title: 'Generate Translated Cover Art',
          description: 'Create translated covers using your existing cover or from scratch.',
        });
      }
      setTry2026Cards(try2026);

      const wordsByLanguage = data.wordsByLanguage || {};
      const languageBreakdown = Object.entries(wordsByLanguage)
        .filter(([, words]) => words > 0)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 6)
        .map(([language, words]) => ({
          language: language.charAt(0).toUpperCase() + language.slice(1),
          words,
        }));

      sampleForm.setFieldsValue({
        year: yearValue,
        summaryLine: `You translated ${formatNumber(data.translatedWords)} words across ${data.translatedBooks} books.`,
        topPercentile: data.topPercentile ?? undefined,
        topPercentileMetric: data.topPercentileMetric ?? 'words translated',
        topPercentileDetail: data.topPercentileDetail ?? undefined,
        disclaimer: sampleValues.disclaimer || DEFAULT_SAMPLE.disclaimer,
        stats: prioritizedStats,
        languageBreakdown,
        try2026,
      });
      message.success('Loaded user stats.');
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      message.error(detail || 'Failed to load user stats.');
    } finally {
      setUserStatsLoading(false);
    }
  };

  const handleSendBatch = async () => {
    const yearValue = sampleForm.getFieldValue('year') || DEFAULT_SAMPLE.year || new Date().getFullYear();
    setSendLoading(true);
    try {
      const { data } = await confirmedCommunication<YearEndEmailSendResponse>(
        `${API_BASE}/admin/year-end-emails/send-batch`,
        {
          year: yearValue,
          jobId: sendJobId || undefined,
          batchSize: sendBatchSize,
        }
      );
      setSendJobId(data.jobId);
      setSendStatus(data);
      message.success('Confirmed batch completed.');
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      message.error(detail || 'Failed to start send batch.');
    } finally {
      setSendLoading(false);
    }
  };

  const handleSendRandomTests = async () => {
    const yearValue = sampleForm.getFieldValue('year') || DEFAULT_SAMPLE.year || new Date().getFullYear();
    setRandomTestLoading(true);
    try {
      const { data } = await confirmedCommunication<YearEndRandomTestSendResponse>(
        `${API_BASE}/admin/year-end-emails/test-send-random`,
        {
          year: yearValue,
          count: 3,
          toEmail: 'contact@scribeshadow.com',
          includeUserIds: [],
        }
      );
      setRandomTestResult(data);
      message.success(`Sent ${data.sent} random test emails.`);
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      message.error(detail || 'Failed to send random tests.');
    } finally {
      setRandomTestLoading(false);
    }
  };

  const handleRunRebuild = async () => {
    const sampleValues = sampleForm.getFieldsValue();
    const yearValue = sampleValues.year || DEFAULT_SAMPLE.year || new Date().getFullYear();
    setRebuildLoading(true);
    try {
      const payload: Record<string, any> = { year: yearValue, batchSize: rebuildBatchSize };
      if (rebuildJobId) {
        payload.jobId = rebuildJobId;
      }
      const { data } = await adminAxios.post<YearEndRebuildResponse>(
        `${API_BASE}/admin/year-end-emails/rebuild`,
        payload
      );
      setRebuildJobId(data.jobId);
      setRebuildStatus(data);
      if (data.done) {
        message.success('Year-end rebuild completed.');
      } else {
        message.success('Batch processed. Click again to continue.');
      }
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      message.error(detail || 'Failed to run rebuild.');
    } finally {
      setRebuildLoading(false);
    }
  };

  const handleResetRebuild = async () => {
    const sampleValues = sampleForm.getFieldsValue();
    const yearValue = sampleValues.year || DEFAULT_SAMPLE.year || new Date().getFullYear();
    setRebuildLoading(true);
    try {
      const payload: Record<string, any> = {
        year: yearValue,
        batchSize: rebuildBatchSize,
        reset: true,
      };
      const { data } = await adminAxios.post<YearEndRebuildResponse>(
        `${API_BASE}/admin/year-end-emails/rebuild`,
        payload
      );
      setRebuildJobId(data.jobId);
      setRebuildStatus(data);
      setAutoRebuild(false);
      message.success('Rebuild reset started.');
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      message.error(detail || 'Failed to reset rebuild.');
    } finally {
      setRebuildLoading(false);
    }
  };

  const handleFetchRebuildCount = async () => {
    const sampleValues = sampleForm.getFieldsValue();
    const yearValue = sampleValues.year || DEFAULT_SAMPLE.year || new Date().getFullYear();
    setRebuildCountLoading(true);
    try {
      const { data } = await adminAxios.get<YearEndRebuildCountResponse>(
        `${API_BASE}/admin/year-end-emails/rebuild-count?year=${yearValue}`
      );
      setRebuildCount(data);
      message.success('Fetched rebuild count.');
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      message.error(detail || 'Failed to fetch rebuild count.');
    } finally {
      setRebuildCountLoading(false);
    }
  };

  const handleRunSnapshot = async () => {
    const sampleValues = sampleForm.getFieldsValue();
    const yearValue = sampleValues.year || DEFAULT_SAMPLE.year || new Date().getFullYear();
    setSnapshotLoading(true);
    try {
      const payload: Record<string, any> = { year: yearValue, batchSize: snapshotBatchSize };
      if (snapshotJobId) {
        payload.jobId = snapshotJobId;
      }
      const { data } = await adminAxios.post<YearEndEmailSnapshotResponse>(
        `${API_BASE}/admin/year-end-emails/precompute`,
        payload
      );
      setSnapshotJobId(data.jobId);
      setSnapshotStatus(data);
      if (data.done) {
        message.success('Snapshot precompute completed.');
      } else {
        message.success('Snapshot batch processed.');
      }
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      message.error(detail || 'Failed to run snapshot precompute.');
    } finally {
      setSnapshotLoading(false);
    }
  };

  const handleResetSnapshot = async () => {
    const sampleValues = sampleForm.getFieldsValue();
    const yearValue = sampleValues.year || DEFAULT_SAMPLE.year || new Date().getFullYear();
    setSnapshotLoading(true);
    try {
      const { data } = await adminAxios.post<YearEndEmailSnapshotResponse>(
        `${API_BASE}/admin/year-end-emails/precompute`,
        {
          year: yearValue,
          batchSize: snapshotBatchSize,
          reset: true,
        }
      );
      setSnapshotJobId(data.jobId);
      setSnapshotStatus(data);
      setAutoSnapshot(false);
      message.success('Snapshot precompute reset.');
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      message.error(detail || 'Failed to reset snapshot precompute.');
    } finally {
      setSnapshotLoading(false);
    }
  };

  useEffect(() => {
    if (!autoRebuild) {
      if (rebuildTimerRef.current) {
        window.clearInterval(rebuildTimerRef.current);
        rebuildTimerRef.current = null;
      }
      return;
    }
    if (rebuildTimerRef.current) {
      return;
    }
    rebuildTimerRef.current = window.setInterval(() => {
      if (!rebuildLoading && !rebuildStatus?.done) {
        handleRunRebuild();
      }
    }, 2500);
    return () => {
      if (rebuildTimerRef.current) {
        window.clearInterval(rebuildTimerRef.current);
        rebuildTimerRef.current = null;
      }
    };
  }, [autoRebuild, rebuildLoading, rebuildStatus, handleRunRebuild]);

  useEffect(() => {
    if (!autoSnapshot) {
      if (snapshotTimerRef.current) {
        window.clearInterval(snapshotTimerRef.current);
        snapshotTimerRef.current = null;
      }
      return;
    }
    if (snapshotTimerRef.current) {
      return;
    }
    snapshotTimerRef.current = window.setInterval(() => {
      if (!snapshotLoading && !snapshotStatus?.done) {
        handleRunSnapshot();
      }
    }, 2500);
    return () => {
      if (snapshotTimerRef.current) {
        window.clearInterval(snapshotTimerRef.current);
        snapshotTimerRef.current = null;
      }
    };
  }, [autoSnapshot, snapshotLoading, snapshotStatus, handleRunSnapshot]);

  useEffect(() => {
    if (!autoSend) {
      if (sendTimerRef.current) {
        window.clearInterval(sendTimerRef.current);
        sendTimerRef.current = null;
      }
      return;
    }
    if (sendTimerRef.current) {
      return;
    }
    sendTimerRef.current = window.setInterval(() => {
      if (!sendLoading && !sendStatus?.done) {
        handleSendBatch();
      }
    }, 300000);
    return () => {
      if (sendTimerRef.current) {
        window.clearInterval(sendTimerRef.current);
        sendTimerRef.current = null;
      }
    };
  }, [autoSend, sendLoading, sendStatus, handleSendBatch]);

  const copyToClipboard = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      message.success(`${label} copied to clipboard.`);
    } catch {
      message.error('Unable to copy to clipboard.');
    }
  };

  const convertFileToBase64 = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });

  const handleImageUpload = async (file: File, field: 'logoImage' | 'mascotImage') => {
    try {
      const base64 = await convertFileToBase64(file);
      configForm.setFieldsValue({ [field]: base64 });
      message.success('Image uploaded.');
    } catch {
      message.error('Failed to upload image.');
    }
    return false;
  };

  const logoValue = Form.useWatch('logoImage', configForm);
  const mascotValue = Form.useWatch('mascotImage', configForm);

  const previewContent = useMemo(() => {
    if (!preview) {
      return <Empty description="Preview not available yet." />;
    }
    return (
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <Card size="small" bordered={false} style={{ background: token.colorFillSecondary }}>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div>
              <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                Subject
              </Text>
              <Input
                readOnly
                value={preview.subject}
                style={{
                  background: token.colorBgContainer,
                  color: token.colorText,
                  borderRadius: 8,
                }}
              />
            </div>
          </Space>
          <Space style={{ marginTop: 16 }}>
            <Button
              icon={<CopyOutlined />}
              size="small"
              onClick={() => copyToClipboard(preview.subject, 'Subject')}
            >
              Copy Subject
            </Button>
            <Button
              icon={<CopyOutlined />}
              size="small"
              onClick={() => copyToClipboard(preview.text, 'Plain text body')}
            >
              Copy Text Body
            </Button>
          </Space>
        </Card>
        <Card size="small" bordered={false} bodyStyle={{ padding: 0, background: token.colorBgLayout }}>
          <iframe
            title="year-end-email-preview"
            srcDoc={preview.html}
            style={{
              width: '100%',
              border: 'none',
              borderRadius: 16,
              minHeight: 740,
              background: token.colorBgContainer,
            }}
          />
        </Card>
        <Card size="small" title="Plain Text">
          <pre style={{ whiteSpace: 'pre-wrap' }}>{preview.text}</pre>
        </Card>
      </Space>
    );
  }, [preview]);

  return (
    <Spin spinning={loading && !loadedConfig}>
      <div className="admin-content-editor">
        <section className="admin-content-editor-header" aria-labelledby="year-end-email-admin-title">
          <div>
            <div className="admin-content-editor-kicker">Email editor</div>
            <h2 id="year-end-email-admin-title">Year-End Email Templates</h2>
            <p>Configure wrapped email branding, rebuild jobs, test sends, and preview output.</p>
          </div>
        </section>
        {inlineError && (
          <Alert
            className="admin-content-editor-inline-alert"
            type="error"
            showIcon
            message="Year-end email editor needs attention"
            description={inlineError}
          />
        )}
      <Row gutter={24} className="admin-content-editor-layout">
        <Col span={10}>
          <Card title="Year-End Email Branding & Sender" className="admin-content-editor-card admin-content-editor-card--editor">
            <Form
              layout="vertical"
              form={configForm}
              initialValues={loadedConfig || {}}
              onFinish={handleSaveConfig}
            >
              <Form.Item label="Brand Name" name="brandName">
                <Input placeholder="Brand name shown in emails" />
              </Form.Item>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item label="Sender Name" name="senderName">
                    <Input placeholder="e.g. ScribeShadow Team" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Sender Email" name="senderEmail">
                    <Input placeholder="support@example.com" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item label="Support Email" name="supportEmail">
                    <Input placeholder="support@example.com" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Dashboard URL" name="dashboardUrl">
                    <Input placeholder="https://app.scribeshadow.com/manage-books" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item label="Primary Color" name="primaryColor">
                    <Input type="color" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Border Color" name="borderColor">
                    <Input type="color" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item label="Text Main" name="textMain">
                    <Input type="color" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Text Muted" name="textMuted">
                    <Input type="color" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item label="Card Background" name="cardBackground">
                    <Input type="color" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Body Background" name="bodyBackground">
                    <Input type="color" />
                  </Form.Item>
                </Col>
              </Row>
              <div className="admin-content-editor-swatches" aria-label="Token color reference">
                <span style={{ background: 'var(--text-info)' }} />
                <span style={{ background: 'var(--surface-base)' }} />
                <span style={{ background: 'var(--border-subtle)' }} />
                <span style={{ background: 'var(--text-main)' }} />
                <Text type="secondary">Use the preview to compare raw email colors against app token references.</Text>
              </div>
              <Divider />
              <Form.Item
                label="Logo Image (URL or upload)"
                name="logoImage"
                extra="Shown centered at the top. Ideal size ~140px wide."
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Input placeholder="Paste an image URL or use upload" />
                  <Space>
                    <Upload
                      beforeUpload={(file) => handleImageUpload(file, 'logoImage')}
                      showUploadList={false}
                      accept="image/*"
                    >
                      <Button icon={<UploadOutlined />}>Upload Image</Button>
                    </Upload>
                    {logoValue && (
                      <Button
                        icon={<DeleteOutlined />}
                        danger
                        onClick={() => configForm.setFieldsValue({ logoImage: '' })}
                      >
                        Remove
                      </Button>
                    )}
                  </Space>
                  {logoValue && (
                    <Image
                      src={logoValue}
                      alt="Logo preview"
                      style={{ marginTop: 8, maxWidth: 160 }}
                      fallback=""
                    />
                  )}
                </Space>
              </Form.Item>
              <Form.Item
                label="Mascot Image (URL or upload)"
                name="mascotImage"
                extra="Appears in the hero panel. Portrait rectangle recommended."
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Input placeholder="Paste an image URL or use upload" />
                  <Space>
                    <Upload
                      beforeUpload={(file) => handleImageUpload(file, 'mascotImage')}
                      showUploadList={false}
                      accept="image/*"
                    >
                      <Button icon={<UploadOutlined />}>Upload Image</Button>
                    </Upload>
                    {mascotValue && (
                      <Button
                        icon={<DeleteOutlined />}
                        danger
                        onClick={() => configForm.setFieldsValue({ mascotImage: '' })}
                      >
                        Remove
                      </Button>
                    )}
                  </Space>
                  {mascotValue && (
                    <Image
                      src={mascotValue}
                      alt="Mascot preview"
                      style={{ marginTop: 8, maxWidth: 220 }}
                      fallback=""
                    />
                  )}
                </Space>
              </Form.Item>
              <Space style={{ width: '100%', justifyContent: 'space-between', marginTop: 8 }}>
                <Button
                  icon={<SaveOutlined />}
                  type="primary"
                  htmlType="submit"
                  loading={saving}
                >
                  Save Config
                </Button>
                <Button
                  onClick={() => loadedConfig && configForm.setFieldsValue(loadedConfig)}
                  disabled={!loadedConfig || saving}
                >
                  Reset to Saved
                </Button>
              </Space>
            </Form>
          </Card>

          <Card title="Sample Inputs" className="admin-content-editor-card admin-content-editor-card--stacked admin-content-editor-card--actions">
            <Form layout="vertical" form={sampleForm} initialValues={DEFAULT_SAMPLE}>
              <Card size="small" style={{ marginBottom: 16, background: token.colorFillSecondary }}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text strong>Year-End Stats Rebuild</Text>
                  <Text type="secondary">
                    Runs one batch of the yearly aggregation. Click again to continue until done.
                  </Text>
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Space>
                      <Button onClick={handleRunRebuild} loading={rebuildLoading} type="primary">
                        {rebuildJobId ? 'Continue Rebuild' : 'Run Rebuild'}
                      </Button>
                      <Button onClick={handleResetRebuild} loading={rebuildLoading} danger>
                        Reset Rebuild
                      </Button>
                      <Button onClick={handleFetchRebuildCount} loading={rebuildCountLoading}>
                        Get Doc Count
                      </Button>
                      <Space>
                        <Text type="secondary">Auto-run</Text>
                        <Switch checked={autoRebuild} onChange={setAutoRebuild} />
                      </Space>
                    </Space>
                    {rebuildStatus && (
                      <Text type="secondary">
                        {rebuildStatus.status} · {rebuildStatus.processedDocs} docs · {rebuildStatus.updatedUsers} users
                      </Text>
                    )}
                  </Space>
                  <Row gutter={12}>
                    <Col span={12}>
                      <InputNumber
                        min={200}
                        max={2000}
                        value={rebuildBatchSize}
                        onChange={(value) => setRebuildBatchSize(Number(value || 0))}
                        style={{ width: '100%' }}
                        placeholder="Batch size"
                      />
                    </Col>
                    <Col span={12}>
                      <Text type="secondary">Larger batches reduce clicks but may be slower.</Text>
                    </Col>
                  </Row>
                  {rebuildCount && (
                    <Text type="secondary">
                      Count: {rebuildCount.count.toLocaleString()} docs ({rebuildCount.source})
                    </Text>
                  )}
                  {rebuildJobId && (
                    <Text type="secondary">Job ID: {rebuildJobId}</Text>
                  )}
                </Space>
              </Card>
              <Card size="small" style={{ marginBottom: 16, background: token.colorFillSecondary }}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text strong>Precompute Email Snapshots</Text>
                  <Text type="secondary">
                    Builds email-ready stats for all users so bulk sends don’t run heavy queries.
                  </Text>
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Space>
                      <Button onClick={handleRunSnapshot} loading={snapshotLoading} type="primary">
                        {snapshotJobId ? 'Continue Precompute' : 'Run Precompute'}
                      </Button>
                      <Button onClick={handleResetSnapshot} loading={snapshotLoading} danger>
                        Reset Precompute
                      </Button>
                      <Space>
                        <Text type="secondary">Auto-run</Text>
                        <Switch checked={autoSnapshot} onChange={setAutoSnapshot} />
                      </Space>
                    </Space>
                    {snapshotStatus && (
                      <Text type="secondary">
                        {snapshotStatus.status} · {snapshotStatus.processedUsers} users · {snapshotStatus.computedUsers} saved
                      </Text>
                    )}
                  </Space>
                  <Row gutter={12}>
                    <Col span={12}>
                      <InputNumber
                        min={10}
                        max={500}
                        value={snapshotBatchSize}
                        onChange={(value) => setSnapshotBatchSize(Number(value || 0))}
                        style={{ width: '100%' }}
                        placeholder="Batch size"
                      />
                    </Col>
                    <Col span={12}>
                      <Text type="secondary">Larger batches reduce clicks but may be slower.</Text>
                    </Col>
                  </Row>
                  {snapshotJobId && (
                    <Text type="secondary">Job ID: {snapshotJobId}</Text>
                  )}
                </Space>
              </Card>
              <Card size="small" style={{ marginBottom: 16, background: token.colorFillSecondary }}>
                <Row gutter={12} align="middle">
                  <Col span={16}>
                    <Input
                      value={userIdInput}
                      onChange={(event) => setUserIdInput(event.target.value)}
                      placeholder="Load stats for user ID..."
                    />
                  </Col>
                  <Col span={8}>
                    <Button
                      onClick={handleLoadUserStats}
                      loading={userStatsLoading}
                      block
                    >
                      Load User Stats
                    </Button>
                  </Col>
                </Row>
              </Card>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item label="User Name" name="userName">
                    <Input placeholder="Avery" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Year" name="year">
                    <InputNumber min={2020} max={2100} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item label="Headline" name="headline">
                <Input placeholder="Your 2024 Wrapped headline" />
              </Form.Item>
              <Form.Item label="Summary Line" name="summaryLine">
                <Input placeholder="Short summary sentence" />
              </Form.Item>
              <Form.Item label="Disclaimer" name="disclaimer">
                <Input placeholder="Stats are based on model usage tracking that began Sep 28, 2025." />
              </Form.Item>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item label="Top Percentile" name="topPercentile">
                    <InputNumber min={1} max={100} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Percentile Metric" name="topPercentileMetric">
                    <Input placeholder="words translated" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item label="Percentile Detail" name="topPercentileDetail">
                <Input placeholder="Additional context for the percentile callout" />
              </Form.Item>
              <Divider />
              <Form.List name="stats">
                {(fields, { add, remove }) => (
                  <Space direction="vertical" style={{ width: '100%' }} size="small">
                    {fields.map((field) => (
                      <Card key={field.key} size="small" style={{ background: token.colorFillSecondary }}>
                        <Row gutter={12}>
                          <Col span={10}>
                            <Form.Item label="Label" name={[field.name, 'label']}>
                              <Input placeholder="Words translated" />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item label="Value" name={[field.name, 'value']}>
                              <Input placeholder="412,000" />
                            </Form.Item>
                          </Col>
                          <Col span={5}>
                            <Button
                              icon={<MinusCircleOutlined />}
                              onClick={() => remove(field.name)}
                              style={{ marginTop: 30 }}
                            >
                              Remove
                            </Button>
                          </Col>
                        </Row>
                        <Form.Item label="Detail (optional)" name={[field.name, 'detail']}>
                          <Input placeholder="Extra context" />
                        </Form.Item>
                      </Card>
                    ))}
                    <Button icon={<PlusOutlined />} onClick={() => add()} block>
                      Add Stat
                    </Button>
                  </Space>
                )}
              </Form.List>
              <Divider />
              <Form.List name="languageBreakdown">
                {(fields, { add, remove }) => (
                  <Space direction="vertical" style={{ width: '100%' }} size="small">
                    {fields.map((field) => (
                      <Card key={field.key} size="small" style={{ background: token.colorFillSecondary }}>
                        <Row gutter={12}>
                          <Col span={11}>
                            <Form.Item label="Language" name={[field.name, 'language']}>
                              <Input placeholder="Spanish" />
                            </Form.Item>
                          </Col>
                          <Col span={9}>
                            <Form.Item label="Words" name={[field.name, 'words']}>
                              <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                          </Col>
                          <Col span={4}>
                            <Button
                              icon={<MinusCircleOutlined />}
                              onClick={() => remove(field.name)}
                              style={{ marginTop: 30 }}
                            >
                              Remove
                            </Button>
                          </Col>
                        </Row>
                      </Card>
                    ))}
                    <Button icon={<PlusOutlined />} onClick={() => add()} block>
                      Add Language Row
                    </Button>
                  </Space>
                )}
              </Form.List>
              <Divider />
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item label="CTA Label" name="ctaLabel">
                    <Input placeholder="Open your library" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="CTA URL (optional)" name="ctaUrl">
                    <Input placeholder="https://app.scribeshadow.com/manage-books" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item label="Recipient Email (test send)" name="recipientEmail">
                <Input type="email" placeholder="wrapped-preview@example.com" />
              </Form.Item>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={refreshPreview}
                  block
                  loading={previewLoading}
                >
                  Refresh Preview
                </Button>
                <Button
                  icon={<MailOutlined />}
                  type="primary"
                  onClick={handleSendTestEmail}
                  block
                  loading={sendingTest}
                >
                  Send Test Email
                </Button>
              </Space>
              <Divider />
              <Title level={5} style={{ marginBottom: 12 }}>
                Bulk Send
              </Title>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item label="Batch size">
                    <InputNumber
                      min={1}
                      max={10}
                      value={sendBatchSize}
                      onChange={(value) => setSendBatchSize(value ?? 10)}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Job ID (resume)">
                    <Input
                      placeholder="auto"
                      value={sendJobId}
                      onChange={(event) => setSendJobId(event.target.value)}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Button
                icon={<MailOutlined />}
                type="primary"
                onClick={handleSendBatch}
                block
                loading={sendLoading}
              >
                Send Batch
              </Button>
              <Space style={{ width: '100%', justifyContent: 'space-between', marginTop: 8 }}>
                <Text type="secondary">Each batch requires confirmation</Text>
                <Switch checked={false} disabled title="Each batch requires recipient confirmation" />
              </Space>
              {sendStatus && (
                <div style={{ marginTop: 12, color: token.colorTextSecondary }}>
                  {sendStatus.status} · {sendStatus.sentCount} sent · {sendStatus.skippedCount} skipped ·{' '}
                  {sendStatus.failedCount} failed · {sendStatus.processedUsers} processed
                </div>
              )}
              <Divider />
              <Title level={5} style={{ marginBottom: 12 }}>
                Random Tests
              </Title>
              <Button
                icon={<MailOutlined />}
                onClick={handleSendRandomTests}
                block
                loading={randomTestLoading}
              >
                Send 10 Random Tests
              </Button>
              {randomTestResult && (
                <div style={{ marginTop: 12, color: token.colorTextSecondary }}>
                  Sent {randomTestResult.sent} · Failed {randomTestResult.failed} · Skipped {randomTestResult.skipped}
                </div>
              )}
            </Form>
          </Card>
        </Col>
        <Col span={14}>
          <Card
            className="admin-content-editor-card admin-content-editor-card--preview"
            title="Email Preview"
            extra={
              <Button
                icon={<ReloadOutlined />}
                onClick={refreshPreview}
                loading={previewLoading}
              >
                Refresh
              </Button>
            }
          >
            <Spin spinning={previewLoading && !preview}>
              {previewContent}
            </Spin>
          </Card>
        </Col>
      </Row>
      </div>
    </Spin>
  );
};

export default YearEndEmailAdmin;
